import PropTypes from 'prop-types';
import React from 'react';
import { isRunningInSEBBrowser } from '@amplify/secure-exam-browser';
import GoButton from '../components/GoButton';
import Playlist from '../components/Playlist';
import FormSelection from '../components/FormSelection';
import Button from '../components/Button';
import Popup from '../components/Popup';
import CommonLayout from '../layouts/commonLayout';
import UserMessage from '../components/UserMessage';
import { RESUME_PROCESSING_MESSAGE } from '../../constants/error';
import { NO_ASSESSMENT_MESSAGE, ARIA_LABELS } from '../../constants/text';
import { ASSESSMENT_TYPE, LC } from '../../constants/measure';
import { LANGUAGE } from '../../constants/language';
import ScreenErrorContainer from '../error/ScreenErrorContainer';
import LoadingPage from '../components/LoadingPage';
import { isIOS, isOSVersionEqualOrGreaterThan, isMacOSSafari13 } from '../utils/browserUtils';
import { redirectToAR } from '../../trc/troUtils';
import { localize, DICTIONARY_KEYS } from '../localization';
import VRHelpPopup from '../components/helpPopup/VRHelpPopup';
import { hasOfflineResults, hasPersistedRecording } from '../persistence/persistanceUtils';
import { LC_PASSAGE_MAPPING } from '../../lc/lcConstants';
import LCDemoVideoContainer from '../../lc/pages/demoVideo/LCDemoVideoContainer';
import { ApolloContext } from '../../ApolloContextProvider';
import { PERIOD } from '../../constants/result';

export default class StartView extends React.Component {
    constructor(props) {
        super(props);
        this.onNext = this.onNext.bind(this);
        this.getButtonText = this.getButtonText.bind(this);
        this.onLogOut = this.onLogOut.bind(this);
        this.updatePassageId = this.updatePassageId.bind(this);
        this.state = {
            showVideoModal: false,
            syncingOfflineResults: false,
        };
    }

    async componentDidMount() {
        const shouldSetNextToyPlaylist = this.props.isAssessmentCompleted && this.props.hasNextToy;
        if (shouldSetNextToyPlaylist) {
            this.props.refreshForms();  // reset forms fetch status so that loading screen was shown
            this.props.setNextToyPlaylist();
        }
        this.setLanguage();
        if (!this.props.isFormsDataFetched || shouldSetNextToyPlaylist) {
            this.props.fetchStudentForms();
        }
        if (this.props.troResult && this.props.isVrResultUnavailable) {
            const hasOfflineAudio = await hasPersistedRecording(this.props.troResult.id);
            if (hasOfflineAudio) {
                const shouldRetry = true;
                this.props.showAudioSavingPopup();
                await this.props
                    .syncOfflineAudio(shouldRetry)
                    .then(() => this.props.getTROResults())
                    .finally(() => {
                        this.props.hideAudioSavingPopup();
                    });
            }
        }

        // We should precache instructions is we are not in SEB
        if (!isRunningInSEBBrowser()) {
            this.props.cacheInstructions(this.props.nextIncompletePlaylistItem);
        }

        await hasOfflineResults(this.props.studentUid)
            .then(hasResultsInStorage => {
                if (hasResultsInStorage) {
                    this.setState({ syncingOfflineResults: true });
                    return this.props.syncOfflineResults();
                }
                // don't change state status
                return null;
            })
            .finally(() => {
                this.setState({ syncingOfflineResults: false });
            });
    }

    componentDidUpdate(prevProps) {
        const {
            troResult,
            didApiCallSucceed,
            didApiCallFail,
            prevApiRequest,
            executeApiCallSucceededEffect,
            executeApiCallFailedEffect,
        } = this.props;
        if (troResult && didApiCallSucceed && !prevProps.didApiCallSucceed) {
            executeApiCallSucceededEffect(prevApiRequest, troResult.id);
        }
        if (troResult && didApiCallFail && !prevProps.didApiCallFail) {
            executeApiCallFailedEffect(prevApiRequest, troResult.id);
        }
        this.setLanguage();
    }

    setLanguage() {
        if (this.props.nextIncompletePlaylistItem) {
            this.props.setCurrentLanguage(this.props.nextIncompletePlaylistItem.language);
        } else if (this.props.lastCompletedPlaylistItem) {
            this.props.setCurrentLanguage(this.props.lastCompletedPlaylistItem.language);
        } else {
            this.props.setCurrentLanguage(LANGUAGE.ENG);
        }
    }

    onNext() {
        const { isExternalURLSourcePresent, beginAssessment } = this.props;
        const element = document.documentElement;
        // removing full screen mode for iOS 12 Safari due to
        // huge amount of bugs found in that version
        // also restricting fullscreen for iOS 13 Safari
        // (iOS 13 Safari on iPad doesn't identify itself as iOS-hosted)
        // removing the fullscreen mode for AR version
        const fullScreenEnabled =
            !(isIOS() && isOSVersionEqualOrGreaterThan([12, 0])) && !isMacOSSafari13() && !isExternalURLSourcePresent;

        if (element && fullScreenEnabled) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        }
        beginAssessment();
        if (!this.props.playlistItemInProgress && isExternalURLSourcePresent) {
            this.props.trackStartAssessment();
        }
    }

    onLogOut() {
        this.props.hideHelpPopup();
        this.props.showSpinner();
        this.props.logUserOut();
    }

    getButtonText() {
        if (this.props.playlistItemInProgress) {
            return DICTIONARY_KEYS.RESUME;
        }
        return DICTIONARY_KEYS.START;
    }

    getButtonLabel() {
        if (this.props.playlistItemInProgress) {
            return ARIA_LABELS.RESUME_ASSESSMENT;
        }
        return ARIA_LABELS.START_ASSESSMENT;
    }

    updatePassageId(passageId) {
        this.props.setSelectedPassageId(passageId.value);
    }

    getAssessmentStatusElements() {
        if (this.props.assessmentType === ASSESSMENT_TYPE.PM && this.props.hasCompletedAllPmForms) {
            return <UserMessage text={localize(DICTIONARY_KEYS.ALL_FORMS_COMPLETED)} />;
        }
        if (this.props.isAssessmentCompleted) {
            if (this.props.isExternalURLSourcePresent) {
                const queryParams = {
                    hasTroError: false,
                    benchmarkMode: this.props.isBenchmarkModeEnabled,
                };
                return redirectToAR(queryParams);
            }
            return <UserMessage text={localize(DICTIONARY_KEYS.DONE)} />;
        }
        if (this.props.isVrResultProcessing && this.props.isComprehensionComplete) {
            return <UserMessage text={RESUME_PROCESSING_MESSAGE} />;
        }
        if (
            this.props.nextIncompletePlaylistItem &&
            this.props.nextIncompletePlaylistItem.measure === LC.MEASURE &&
            !this.props.lcPassageId
        ) {
            const { showVideoModal } = this.state;

            const setVideoModalClose = () => this.setState({ showVideoModal: false });
            const setVideoModalOpen = () => this.setState({ showVideoModal: true });

            const demoButtonText = (
                <div className="in-line">
                    <div className="amp-styles">
                        <div className="icon-v2-video" />
                    </div>
                    Watch Demo
                </div>
            );

            const closeModalButton = (
                <div className="amp-styles">
                    <div className="icon-v2-close" />
                </div>
            );
            return (
                // eslint-disable-next-line react/jsx-fragments
                <React.Fragment>
                    <div className="lc-selection">
                        <div>
                            <FormSelection
                                message="Please select a form for Listening Comprehension"
                                placeholder="Select form"
                                options={LC_PASSAGE_MAPPING[this.props.toy][
                                    this.props.grade
                                ].passages.map(passage => ({
                                    label: passage.title,
                                    value: passage.id,
                                }))}
                                onChange={this.updatePassageId}
                            />
                        </div>
                        <div className="center-container">
                            <Button
                                className="demo-button"
                                onClick={setVideoModalOpen}
                                text={demoButtonText}
                                ariaLabel="Open Modal"
                            />
                        </div>
                    </div>
                    {showVideoModal && (
                        <div className="modal-container">
                            <Popup backgroundDim="full">
                                <div className="video-container">
                                    <LCDemoVideoContainer
                                        isVideoWatchedInPreviousSession={false}
                                    />
                                </div>
                                <Button
                                    className="close-button"
                                    onClick={setVideoModalClose}
                                    text={closeModalButton}
                                    ariaLabel="Close Modal"
                                />
                            </Popup>
                        </div>
                    )}
                </React.Fragment>
            );
        }
        return (
            <GoButton ariaLabel={localize(this.getButtonLabel())} text={localize(this.getButtonText())} onClick={this.onNext} />
        );
    }

    getAssessmentTitle() {
        if (this.props.period === PERIOD.SUMMER) {
            return `${this.context.fourthWindowLabel} Benchmark`;
        }

        return this.props.periodFullText;
    }

    render() {
        if (!this.props.isFormsDataFetched || this.state.syncingOfflineResults || this.context.fourthWindowLabelLoading) {
            return <LoadingPage />;
        }

        return (
            <CommonLayout class="start">
                <ScreenErrorContainer onRetry={this.props.beginAssessment} />
                {this.props.playlistForRender.length > 0 ? (
                    <div className="container">
                        {this.props.isHelpPopupVisible && (
                            <VRHelpPopup
                                logUserOut={this.onLogOut}
                                resultsVRStatuses={this.props.resultsVRStatuses}
                                resultVRErrorReasons={this.props.resultVRErrorReasons}
                                isAudioSavingInProgress={this.props.isOfflineAudioSyncInProgress}
                                isExternalURLSourcePresent={this.props.isExternalURLSourcePresent}
                                isBenchmarkModeEnabled={this.props.isBenchmarkModeEnabled}
                                trackTroError={this.props.trackTroError}
                                trackTroVrPopup={this.props.trackTroVrPopup}
                            />
                        )}
                        <p className="assessment-title">{this.getAssessmentTitle()}</p>
                        {this.getAssessmentStatusElements()}
                        {this.props.assessmentType === ASSESSMENT_TYPE.BM && (
                            <Playlist playlistForRender={this.props.playlistForRender} />
                        )}
                    </div>
                ) : (
                    <UserMessage text={NO_ASSESSMENT_MESSAGE} />
                )}
            </CommonLayout>
        );
    }
}

StartView.propTypes = {
    beginAssessment: PropTypes.func.isRequired,
    playlistItemInProgress: PropTypes.object,
    isAssessmentCompleted: PropTypes.bool,
    isFormsDataFetched: PropTypes.bool,
    fetchStudentForms: PropTypes.func.isRequired,
    playlistForRender: PropTypes.array,
    period: PropTypes.string,
    periodFullText: PropTypes.string,
    grade: PropTypes.string,
    toy: PropTypes.string,
    lcPassageId: PropTypes.string,
    assessmentType: PropTypes.oneOf([ASSESSMENT_TYPE.BM, ASSESSMENT_TYPE.PM]),
    setCurrentLanguage: PropTypes.func,
    setSelectedPassageId: PropTypes.func,
    nextIncompletePlaylistItem: PropTypes.object,
    lastCompletedPlaylistItem: PropTypes.object,
    isVrResultProcessing: PropTypes.bool,
    isComprehensionComplete: PropTypes.bool,
    hideHelpPopup: PropTypes.func,
    showSpinner: PropTypes.func,
    logUserOut: PropTypes.func,
    isHelpPopupVisible: PropTypes.bool,
    resultsVRStatuses: PropTypes.array,
    resultVRErrorReasons: PropTypes.array,
    hasCompletedAllPmForms: PropTypes.bool,
    troResult: PropTypes.object,
    isVrResultUnavailable: PropTypes.bool,
    showAudioSavingPopup: PropTypes.func,
    syncOfflineAudio: PropTypes.func,
    getTROResults: PropTypes.func,
    hideAudioSavingPopup: PropTypes.func,
    isOfflineAudioSyncInProgress: PropTypes.bool,
    didApiCallSucceed: PropTypes.bool,
    didApiCallFail: PropTypes.bool,
    prevApiRequest: PropTypes.object,
    executeApiCallSucceededEffect: PropTypes.func,
    executeApiCallFailedEffect: PropTypes.func,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    setNextToyPlaylist: PropTypes.func,
    trackStartAssessment: PropTypes.func,
    hasNextToy: PropTypes.bool,
    trackTroError: PropTypes.func,
    trackTroVrPopup: PropTypes.func,
    refreshForms: PropTypes.func,
    studentUid: PropTypes.string.isRequired,
    syncOfflineResults: PropTypes.func.isRequired,
    cacheInstructions: PropTypes.func.isRequired,
};

StartView.defaultProps = {
    isAssessmentCompleted: false,
    isFormsDataFetched: true,
    playlistItemInProgress: undefined,
    period: '',
    periodFullText: '',
    grade: '',
    toy: '',
    lcPassageId: undefined,
    playlistForRender: [],
    assessmentType: ASSESSMENT_TYPE.BM,
    setCurrentLanguage: () => { },
    setSelectedPassageId: () => { },
    nextIncompletePlaylistItem: undefined,
    lastCompletedPlaylistItem: undefined,
    isVrResultProcessing: false,
    isComprehensionComplete: false,
    hideHelpPopup: () => { },
    showSpinner: () => { },
    logUserOut: () => { },
    isHelpPopupVisible: false,
    resultsVRStatuses: [],
    resultVRErrorReasons: [],
    hasCompletedAllPmForms: false,
    troResult: null,
    isVrResultUnavailable: false,
    showAudioSavingPopup: () => { },
    syncOfflineAudio: () => { },
    getTROResults: () => { },
    hideAudioSavingPopup: () => { },
    isOfflineAudioSyncInProgress: false,
    didApiCallSucceed: false,
    didApiCallFail: false,
    prevApiRequest: {},
    executeApiCallSucceededEffect: () => { },
    executeApiCallFailedEffect: () => { },
    setNextToyPlaylist: () => { },
    trackStartAssessment: () => { },
    hasNextToy: false,
    trackTroError: () => { },
    trackTroVrPopup: () => { },
    refreshForms: () => { },
};

StartView.contextType = ApolloContext;
